import {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  useImperativeHandle
} from 'react';
import type { PropsWithoutRef, MouseEvent } from 'react';

import {
  useConsolidatedRef,
  useUID,
  useActiveDescendant,
  useLazyDescendant,
  useI18n,
  usePrevious,
  useDirection,
  useTestIds
} from '../../hooks';
import type { ForwardRefForwardPropsComponent } from '../../types';
import { cap, withTestIds, getActiveElement } from '../../utils';
import VisuallyHiddenText from '../VisuallyHiddenText';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import Input from '../Input';
import HiddenText from '../HiddenText';

import FlyoutMenuList from './FlyoutMenuList';
import MenuContext from './Menu.context';
import {
  StyledMenu,
  StyledMenuFilter,
  StyledMenuListWrapper,
  StyledFilterIcon
} from './Menu.styles';
import { getMenuTestIds } from './Menu.test-ids';
import type {
  MenuContextProps,
  MenuProps,
  MenuItemProps,
  AcceptedMouseEventElement
} from './Menu.types';
import MenuList from './MenuList';

const Menu: ForwardRefForwardPropsComponent<MenuProps> = forwardRef(function Menu(
  props: PropsWithoutRef<MenuProps>,
  ref: MenuProps['ref']
) {
  const uid = useUID();
  const {
    testId,
    id = uid,
    items = [],
    itemLayout = 'stacked',
    onCreateNew,
    mode = 'action',
    accent,
    scrollAt = 7,
    emptyText,
    onItemClick,
    onItemActive,
    onItemExpand,
    loadMore,
    onItemCollapse,
    loading = false,
    currentItemId,
    header,
    footer,
    variant = 'drill-down',
    focusControlEl,
    arrowNavigationUnsupported,
    'aria-describedby': ariaDescribedBy,
    'aria-label': label,
    role = 'menu',
    listId,
    pauseDescendantEvaluation,
    cycle = true,
    filterInputProps,
    menuList: customMenuList,
    handleRef,
    ...restProps
  } = props;

  const testIds = useTestIds(testId, getMenuTestIds);

  const t = useI18n();
  const instructionTextId = useUID();

  const { end: endDirection, start: startDirection } = useDirection();
  const radioName = useUID();
  const menuRef = useConsolidatedRef(ref);
  const filterElRef = useConsolidatedRef<HTMLInputElement>(filterInputProps?.ref);
  const previousItemCount = useRef(0);
  const [activeDescendantUpdateId, setActiveDescendantUpdateId] = useState(0);
  const [focusDescendant, setFocusDescendant] = useState<HTMLElement | null>(null);
  const [flyOutActiveIdStack, setFlyoutActiveIdStack] = useState<string[]>([id]);
  const [focusReturnEl, setFocusReturnEl] = useState<HTMLElement | null>();
  const [preventInitialScroll, setPreventInitialScroll] = useState(false);
  const [flyoutFocusReturnStack, setFlyoutFocusReturnStack] = useState<(HTMLElement | null)[]>([]);
  const [expandToParentItem, setExpandToParentItem] = useState<string | undefined>();

  const menuAsFocusControl = !focusControlEl;

  const activeFlyoutSelector = useMemo(
    () => `fieldset[data-flyout-menu-id="${flyOutActiveIdStack[flyOutActiveIdStack.length - 1]}"]`,
    [flyOutActiveIdStack]
  );

  const focusControl = useMemo(() => {
    if (filterElRef.current) {
      return filterElRef.current;
    }
    return focusControlEl || menuRef.current;
  }, [focusControlEl, menuRef.current]);

  useEffect(() => {
    filterElRef.current?.focus();
  }, []);

  useImperativeHandle(
    handleRef,
    () => {
      return {
        expandTo: itemId => {
          setExpandToParentItem(itemId);
        }
      };
    },
    []
  );

  const pushFlyoutId = useCallback(
    (flyoutId: string) => {
      setFlyoutActiveIdStack([...flyOutActiveIdStack, flyoutId]);
    },
    [flyOutActiveIdStack]
  );

  useEffect(() => {
    setFlyoutActiveIdStack([...flyOutActiveIdStack, id]);
  }, [id]);

  const updateParentDescendantStack = useCallback(
    (item: HTMLElement | null) => {
      setFlyoutFocusReturnStack([...flyoutFocusReturnStack, item]);
    },
    [flyoutFocusReturnStack]
  );

  const getScopedItemId = useCallback(
    (itemId: string) => {
      return btoa(encodeURIComponent(itemId));
    },
    [id]
  );

  const getUnscopedItemId = useCallback(
    (itemId: string) => {
      return decodeURIComponent(atob(itemId));
    },
    [id]
  );

  const updateActiveDescendants: MenuContextProps['updateActiveDescendants'] = useCallback(
    ({ preventScroll = false } = {}) => {
      setPreventInitialScroll(preventScroll);
      setActiveDescendantUpdateId(Math.random());
    },
    []
  );

  const uadConfig = useMemo(
    () => ({
      focusEl: focusControl,
      scope: menuRef.current,
      scopeSelector:
        variant === 'drill-down'
          ? ('fieldset[aria-hidden="false"]' as keyof HTMLElementTagNameMap)
          : activeFlyoutSelector,
      selector: `[role="${
        role === 'menu' ? 'menuitem' : 'option'
      }"], legend` as keyof HTMLElementTagNameMap,
      focusDescendantEl: focusDescendant,
      clearFocusDescendant: () => {
        setFocusDescendant(null);
      },
      focusReturnEl,
      clearFocusReturn: () => {
        setFocusReturnEl(null);
      },
      currentDescendantId: currentItemId ? getScopedItemId(currentItemId) : undefined,
      preventInitialScroll,
      clearPreventScroll: () => {
        setPreventInitialScroll(false);
      },
      pauseDescendantEvaluation,
      cycle
    }),
    [
      focusControl,
      menuRef.current,
      activeFlyoutSelector,
      focusReturnEl,
      focusDescendant,
      currentItemId,
      preventInitialScroll,
      pauseDescendantEvaluation,
      cycle
    ]
  );

  const { activeDescendant, descendants } = useActiveDescendant(uadConfig, [
    activeDescendantUpdateId
  ]);

  const previousActiveDescendant = usePrevious(activeDescendant);

  useLazyDescendant({
    loading,
    descendants,
    previousActiveDescendant,
    activeDescendant,
    focusReturnEl,
    setFocusReturnEl,
    scrollEl: uadConfig.scope?.querySelector(uadConfig.scopeSelector)?.querySelector('ul')
  });

  // ## Bind Menu-specific navigation keyDown.
  useEffect(() => {
    const expandOrCollapse: (action?: 'collapse' | 'expand' | null) => void = (action = null) => {
      if (activeDescendant) {
        // expand
        if (activeDescendant.dataset.expand === 'true' && action !== 'collapse') {
          const clickEl =
            activeDescendant.querySelector<HTMLSpanElement>('span[aria-hidden="true"]') ??
            activeDescendant;
          clickEl.click();
          return;
        }

        // collapse
        if (action !== 'expand') {
          if (
            variant === 'flyout' &&
            flyOutActiveIdStack.length > 1 &&
            flyoutFocusReturnStack.length > 0
          ) {
            setFlyoutActiveIdStack([...flyOutActiveIdStack].slice(0, -1));

            const parentDescendant = flyoutFocusReturnStack.pop();
            if (parentDescendant !== undefined) setFocusReturnEl(parentDescendant);
            updateActiveDescendants();
          } else if (activeDescendant.dataset.collapse === 'true') activeDescendant.click();
        }
      }
    };

    const additionalKeydown = (e: KeyboardEvent) => {
      switch (e.key) {
        case `Arrow${cap(endDirection)}`: {
          if (arrowNavigationUnsupported) break;
          expandOrCollapse('expand');
          break;
        }
        case `Arrow${cap(startDirection)}`: {
          if (arrowNavigationUnsupported) break;
          expandOrCollapse('collapse');
          break;
        }
        case 'Escape': {
          if (
            variant === 'flyout' &&
            flyOutActiveIdStack.length > 1 &&
            flyoutFocusReturnStack.length > 0
          ) {
            e.preventDefault();
            e.stopPropagation();

            setFlyoutActiveIdStack([...flyOutActiveIdStack].slice(0, -1));
            const parentDescendant = flyoutFocusReturnStack.pop();
            if (parentDescendant !== undefined) setFocusReturnEl(parentDescendant);
            updateActiveDescendants();
          }
          break;
        }
        default:
          break;
      }

      if (arrowNavigationUnsupported && (e.key === ' ' || e.key === 'Spacebar') && e.shiftKey) {
        e.preventDefault();
        expandOrCollapse();
      }
    };

    if (activeDescendant) onItemActive?.(getUnscopedItemId(activeDescendant.id));

    focusControl?.addEventListener('keydown', additionalKeydown);

    return () => focusControl?.removeEventListener('keydown', additionalKeydown);
  }, [focusControl, activeDescendant, flyOutActiveIdStack]);

  // ## Update useActiveDescendant on change of items, not selection of items.
  useEffect(() => {
    // Next tick for DOM updates.
    const timeoutId = setTimeout(() => {
      if (items.length === previousItemCount.current) {
        const previousDescendantIds = descendants?.map(node => node.id);
        const newDescendants = uadConfig.scope
          ?.querySelector(uadConfig.scopeSelector)
          ?.querySelectorAll(uadConfig.selector);

        if (!newDescendants) {
          updateActiveDescendants();
          return;
        }

        const scopedDescendants = Array.from(newDescendants);

        if (scopedDescendants?.length !== previousDescendantIds?.length) {
          updateActiveDescendants();
          return;
        }

        const itemsUnchanged = scopedDescendants.every(
          (node, index) => node.id === previousDescendantIds[index]
        );

        if (!itemsUnchanged) {
          updateActiveDescendants();
        }

        return;
      }

      previousItemCount.current = items.length;
      updateActiveDescendants();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [items]);

  // Cannot use previousActiveDescendant here because usePrevious uses useLayoutEffect, which runs before useEffect.
  // This means by the time any useEffect fires, previousActiveDescendant has already been updated to the current value.
  // So previousActiveDescendant can't be used to track the previous active element inside an effect.
  // So, we are using a ref to store the last active descendant instead.
  const lastActiveDescendantRef = useRef<HTMLElement | undefined>();

  useEffect(() => {
    const scopeIsActive = menuRef.current?.getAttribute('data-active-scope') === 'true';
    const focusControlIsActive = getActiveElement() === focusControl;

    if (!scopeIsActive && !focusControlIsActive) {
      lastActiveDescendantRef.current = undefined;
      return;
    }

    const prev = lastActiveDescendantRef.current;

    if (prev && prev !== activeDescendant) {
      prev.dispatchEvent(new FocusEvent('focusout'));
    }

    if (activeDescendant && prev !== activeDescendant) {
      activeDescendant.dispatchEvent(new FocusEvent('focusin'));
    }

    lastActiveDescendantRef.current = activeDescendant;
  }, [activeDescendant, focusControl]);

  useEffect(() => {
    const handleBlur = () => {
      const last = lastActiveDescendantRef.current;
      if (last) {
        last.dispatchEvent(new FocusEvent('focusout'));
        lastActiveDescendantRef.current = undefined;
      }
    };

    focusControl?.addEventListener('blur', handleBlur);
    return () => focusControl?.removeEventListener('blur', handleBlur);
  }, [focusControl]);

  const contextValue = useMemo(() => {
    return {
      componentId: id,
      'aria-label': label,
      mode,
      arrowNavigationUnsupported,
      onItemClick: (
        clickedId: MenuItemProps['id'],
        event: MouseEvent<AcceptedMouseEventElement>
      ) => {
        if (menuAsFocusControl) {
          menuRef.current?.focus();
        }

        onItemClick?.(clickedId, event);
      },
      onItemActive,
      onItemExpand,
      onItemCollapse,
      itemLayout,
      accent,
      scrollAt,
      emptyText,
      radioName,
      loadMore,
      loading,
      variant,
      focusControl,
      updateActiveDescendants,
      setFocusReturnEl,
      setFocusDescendant,
      getScopedItemId,
      pushFlyoutId,
      flyOutActiveIdStack,
      updateParentDescendantStack,
      expandTo: {
        parentItemId: expandToParentItem,
        onComplete: () => {
          setExpandToParentItem(undefined);
        }
      }
    };
  }, [
    id,
    label,
    mode,
    arrowNavigationUnsupported,
    onItemClick,
    onItemActive,
    onItemExpand,
    onItemCollapse,
    itemLayout,
    accent,
    scrollAt,
    emptyText,
    radioName,
    loadMore,
    loading,
    variant,
    focusControl,
    updateActiveDescendants,
    setFocusReturnEl,
    setFocusDescendant,
    getScopedItemId,
    pushFlyoutId,
    flyOutActiveIdStack,
    updateParentDescendantStack,
    expandToParentItem
  ]);

  const menuList =
    customMenuList ??
    (variant === 'drill-down' ? (
      <MenuList
        items={items}
        id={listId}
        menuRole={role}
        aria-multiselectable={role === 'listbox' ? mode === 'multi-select' : undefined}
        aria-labelledby={label ? `${id}-menuLabel` : undefined}
      />
    ) : (
      <FlyoutMenuList items={items} menuRole={role} />
    ));

  return (
    <StyledMenu
      data-testid={testIds.root}
      id={id}
      aria-describedby={menuAsFocusControl ? `${id}-menuDescription` : undefined}
      {...restProps}
      ref={menuRef}
      tabIndex={menuAsFocusControl ? 0 : undefined}
    >
      {menuAsFocusControl && (
        <span id={`${id}-menuDescription`} hidden>
          {(`${t('menu_selection_instructions')} ` && ariaDescribedBy) || ''}
        </span>
      )}
      {label && <VisuallyHiddenText id={`${id}-menuLabel`}>{label}</VisuallyHiddenText>}

      {(filterInputProps || header) && (
        <header
          data-popover-scroll-el
          onMouseDownCapture={e => {
            if (filterElRef.current && e.target !== filterElRef.current) {
              e.preventDefault();
            }
          }}
        >
          {filterInputProps ? (
            <StyledMenuFilter>
              <StyledFilterIcon name='search' />
              <Input
                {...filterInputProps}
                type='search'
                role='combobox'
                aria-label={t('menu_filter_instructions')}
                aria-controls={listId}
                aria-expanded='true'
                aria-describedby={instructionTextId}
                ref={filterElRef}
              />
              <HiddenText id={instructionTextId}>{t('search_instructions')}</HiddenText>
            </StyledMenuFilter>
          ) : (
            header
          )}
        </header>
      )}

      <StyledMenuListWrapper data-popover-scroll-el>
        <MenuContext.Provider value={contextValue}>{menuList}</MenuContext.Provider>
      </StyledMenuListWrapper>
      {(onCreateNew || footer) && (
        <footer data-popover-scroll-el>
          {onCreateNew ? (
            <Button
              data-testid={testIds.createNew}
              variant='link'
              onClick={() => {
                (typeof onCreateNew !== 'function' ? onCreateNew.handler : onCreateNew)();
              }}
              disabled={typeof onCreateNew !== 'function' ? onCreateNew.disabled : undefined}
            >
              <Icon name='plus' />{' '}
              {typeof onCreateNew !== 'function' && onCreateNew.text
                ? onCreateNew.text
                : t('create_new')}
            </Button>
          ) : (
            footer
          )}
        </footer>
      )}
    </StyledMenu>
  );
});

export default withTestIds(Menu, getMenuTestIds);
